// @mui
import { Button, Container } from '@mui/material';
// sections
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { ICompany } from 'src/@types/company';
import Iconify from 'src/components/iconify';
import { getCompanies } from 'src/redux/slices/company';
import CompanyNewEditForm from 'src/sections/@dashboard/companies/CompanyNewEditForm';
import { IUser } from '../../../@types/user';
import { useAuthContext } from '../../../auth/useAuthContext';
import CustomBreadcrumbs from '../../../components/custom-breadcrumbs';
// components
import { useSettingsContext } from '../../../components/settings';
import { useLocales } from '../../../locales';
// routes
import { PATH_DASHBOARD, PATH_PAGE } from '../../../routes/paths';
import { PERMISSIONS, hasPermission } from '../../../utils/permissions';

// ----------------------------------------------------------------------

export default function CompanyEditPage() {
  const { translate } = useLocales();
  const { themeStretch } = useSettingsContext();

  const { id } = useParams();

  const [currentCompany, setCurrentCompany] = useState<ICompany | undefined>(
    undefined
  );

  const { user } = useAuthContext();
  const navigate = useNavigate();

  if (!hasPermission(user as IUser, PERMISSIONS.companyEdit)) {
    navigate(PATH_PAGE.page403);
  }

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;

      const companies = await getCompanies({
        id: {
          _eq: id,
        },
      });

      setCurrentCompany(companies?.[0]);
    };

    fetchUser();
  }, [id]);

  return (
    <>
      <Button
        color="inherit"
        startIcon={<Iconify width={16} icon={'eva:arrow-ios-back-fill'} />}
        sx={{
          height: 40,
          flexShrink: 0,
          borderRadius: 0,
          typography: 'overline',
          color: 'text.disabled',
          justifyContent: 'space-between',
          p: (theme) => theme.spacing(1, 1.5, 1, 2.5),
        }}
        onClick={() => navigate(-1)}
      >
        {`${translate('backward')}`}
      </Button>
      <Helmet>
        <title> {`${translate('companyEditPage.title')}`} </title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading={`${translate('companyEditPage.heading')}`}
          links={[
            {
              name: `${translate('dashboardName')}`,
              href: PATH_DASHBOARD.root,
            },
            {
              name: `${translate('companyEditPage.navTitle')}`,
              href: PATH_DASHBOARD.companies.list,
            },
            { name: `${currentCompany?.name}` },
          ]}
        />

        <CompanyNewEditForm isEdit currentCompany={currentCompany} />
      </Container>
    </>
  );
}
